/**
 * ManualReadingStore — browser IndexedDB store for manual reagent test readings.
 *
 * Uses the `idb` package for a clean typed API. Implements add/update/delete/list/
 * getById/clearAll/subscribe, plus Log Pro migration from localStorage['aquariumLogs_v3'].
 */
import { openDB, type IDBPDatabase } from 'idb';
import type { ManualReading, ReadingFilter } from '@reefapp/shared';
import { slugify } from '@reefapp/shared';
import { ManualReadingError } from '@reefapp/shared';

const DB_NAME = 'reefapp-manual-readings';
const DB_VERSION = 1;
const STORE_NAME = 'readings';

type Listener = (snapshot: readonly ManualReading[]) => void;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by-kitId', 'kitId');
          store.createIndex('by-parameter', 'parameter');
          store.createIndex('by-brand', 'brand');
          store.createIndex('by-timestamp', 'timestamp');
        }
      },
    });
  }
  return dbPromise;
}

// ─── Subscriber system ─────────────────────────────────────────────────

const listeners = new Set<Listener>();

function notify(): void {
  void (async () => {
    try {
      const snapshot = await list();
      for (const fn of listeners) {
        fn(snapshot);
      }
    } catch {
      /* noop — don't crash subscribers */
    }
  })();
}

// ─── ID generation (ULID-style timestamp + random) ─────────────────────

function generateId(): string {
  const ts = Date.now().toString(36).padStart(10, '0');
  const rand = Math.random().toString(36).substring(2, 12);
  return `${ts}-${rand}`;
}

// ─── Public API ────────────────────────────────────────────────────────

export async function add(
  reading: Omit<ManualReading, 'id' | 'timestamp'>,
): Promise<ManualReading> {
  const db = await getDb();
  const now = new Date().toISOString();
  const record: ManualReading = {
    ...reading,
    id: generateId(),
    timestamp: now,
  };
  await db.add(STORE_NAME, record);
  notify();
  return record;
}

export async function update(
  id: string,
  patch: Partial<Pick<ManualReading, 'notes'>>,
): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const existing = await store.get(id);
  if (!existing) {
    throw new ManualReadingError(`Reading not found: ${id}`);
  }
  const updated = { ...existing, ...patch };
  await store.put(updated);
  await tx.done;
  notify();
}

/**
 * Bulk-upsert fully-formed records, preserving their existing ids.
 * Used by the Pi sync reconcile (readingSync) to import server readings.
 */
export async function putMany(records: readonly ManualReading[]): Promise<void> {
  if (records.length === 0) return;
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  for (const record of records) {
    await store.put(record);
  }
  await tx.done;
  notify();
}

export async function remove(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_NAME, id);
  notify();
}

export async function list(filter?: ReadingFilter): Promise<ManualReading[]> {
  const db = await getDb();
  // Use index when filtering by a single known field; otherwise full scan
  if (filter?.parameter && !filter.brand) {
    const index = db.transaction(STORE_NAME).store.index('by-parameter');
    let cursor = await index.openCursor(filter.parameter);
    const results: ManualReading[] = [];
    while (cursor) {
      if (passesFilter(cursor.value, filter)) {
        results.push(cursor.value);
      }
      cursor = await cursor.continue();
    }
    return results.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }

  // Full scan with in-memory filter
  const all = await db.getAll(STORE_NAME);
  const filtered = filter ? all.filter((r) => passesFilter(r, filter)) : all;
  return filtered.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}

export async function getById(id: string): Promise<ManualReading | null> {
  const db = await getDb();
  const record = await db.get(STORE_NAME, id);
  return record ?? null;
}

export async function clearAll(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  await tx.objectStore(STORE_NAME).clear();
  await tx.done;
  notify();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  // Immediately send current snapshot
  void (async () => {
    try {
      const snapshot = await list();
      listener(snapshot);
    } catch {
      /* noop */
    }
  })();
  return () => {
    listeners.delete(listener);
  };
}

// ─── Filter helper ─────────────────────────────────────────────────────

function passesFilter(r: ManualReading, f: ReadingFilter): boolean {
  if (f.parameter && r.parameter !== f.parameter) return false;
  if (f.brand && r.brand !== f.brand) return false;
  if (f.sinceIso && r.timestamp < f.sinceIso) return false;
  if (f.untilIso && r.timestamp > f.untilIso) return false;
  return true;
}

// ─── Log Pro migration ─────────────────────────────────────────────────

const LOGPRO_KEY = 'aquariumLogs_v3';
const MIGRATION_MARKER = 'reefapp:logpro-migration-v1';

/** Check and run the Log Pro migration once. */
export async function migrateFromLogPro(): Promise<number> {
  if (localStorage.getItem(MIGRATION_MARKER) === 'done') return 0;
  const raw = localStorage.getItem(LOGPRO_KEY);
  if (!raw) return 0;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return 0;
  }

  if (!Array.isArray(parsed)) return 0;

  const db = await getDb();
  const existingCount = await db.count(STORE_NAME);
  if (existingCount > 0) {
    // Already has data — skip migration
    localStorage.setItem(MIGRATION_MARKER, 'done');
    return 0;
  }

  let imported = 0;
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  for (const entry of parsed as Record<string, unknown>[]) {
    try {
      const brand = String(entry.brand ?? 'unknown');
      const parameter = String(entry.parameter ?? 'unknown');
      const kitId = `${slugify(brand)}-${slugify(parameter)}`;
      const value = Number(entry.value);
      const unit = String(entry.unit ?? '');

      if (!Number.isFinite(value) || !unit) continue;

      const record: ManualReading = {
        id: generateId(),
        timestamp: String(entry.timestamp ?? entry.date ?? new Date().toISOString()),
        kitId,
        brand,
        parameter,
        value,
        unit,
        notes: entry.notes ? String(entry.notes) : undefined,
      };

      await store.add(record);
      imported++;
    } catch {
      // Skip malformed entries
    }
  }

  await tx.done;
  localStorage.setItem(MIGRATION_MARKER, 'done');
  if (imported > 0) notify();
  return imported;
}